import capitalize from 'lodash/capitalize';
import isBoolean from 'lodash/isBoolean';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import moment from 'moment';

import {FEATURE_GATES_UNLIMITED} from '../../../report/constants';
import {pluralize} from '../../../util';
import {formatDate, getCurrentMoment, parseDate} from '../../../util/date';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  NOW,
  START_OF_TODAY,
  START_OF_YESTERDAY,
  PRESET_DATE_RANGE_DISPLAY_NAMES,
  PresetDateRange,
} from './presets';
import {
  AbsoluteDate,
  ApiDateRange,
  BaseDate,
  BookmarkDateRange,
  CalendarDateRange,
  DateRangeType,
  RelativeDate,
  TimeUnit,
} from './types';

const DATE_RANGE_ATTRIBUTES: Array<keyof DateRange> = [`type`, `from`, `to`, `window`, `rolling`];

const SPECIAL_DATE_RESOLVERS = {
  [START_OF_YESTERDAY]: () =>
    getCurrentMoment({localized: true})
      .subtract(1, `days`)
      .startOf(`day`),
  [END_OF_YESTERDAY]: () =>
    getCurrentMoment({localized: true})
      .subtract(1, `days`)
      .endOf(`day`),
  [START_OF_TODAY]: () => getCurrentMoment({localized: true}).startOf(`day`),
  [END_OF_TODAY]: () => getCurrentMoment({localized: true}).endOf(`day`),
  [NOW]: () => getCurrentMoment({localized: true}),
};

const CALENDAR_DATE_FORMAT = `YYYY-MM-DD`;

export default class DateRange {
  public static get presetOptions() {
    return [];
  }

  public static convertAbsoluteDateToCalendarDate(date: AbsoluteDate): AbsoluteDate {
    return (SPECIAL_DATE_RESOLVERS[date] || (() => moment(date)))().format(CALENDAR_DATE_FORMAT);
  }

  public static convertCalendarDateToRelativeDate(dateStr: string): RelativeDate {
    if (!dateStr) {
      return null;
    }

    const currMoment = getCurrentMoment({localized: true}).startOf(`day`);
    const calendarMoment = moment(dateStr, CALENDAR_DATE_FORMAT).startOf(`day`);
    const daysDiff = currMoment.diff(calendarMoment, `days`);
    return {
      unit: TimeUnit.Day,
      value: daysDiff,
    };
  }

  public static convertRelativeDateToCalendarDate(relativeDate: RelativeDate): string {
    if (
      !relativeDate ||
      ![TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month, TimeUnit.Year].includes(relativeDate.unit)
    ) {
      return null;
    }

    return getCurrentMoment({localized: true})
      .subtract(relativeDate.value, relativeDate.unit)
      .format(CALENDAR_DATE_FORMAT);
  }

  public static convertBaseDateToCalendarDate(baseDate?: BaseDate): string | null {
    if (!baseDate) {
      return null;
    }
    if (DateRange.isRelativeDate(baseDate)) {
      return DateRange.convertRelativeDateToCalendarDate(baseDate);
    }
    return baseDate;
  }

  public static isRelativeDate(date: BaseDate): date is RelativeDate {
    return date.hasOwnProperty(`unit`);
  }

  public static fromBookmarkFormat(bookmarkDateRange: BookmarkDateRange): DateRange {
    const {to_date: toDate, from_date: fromDate, type} = bookmarkDateRange;
    const attrs: Partial<DateRange> = {type};
    switch (type) {
      case DateRangeType.RelativeAfter: {
        attrs.window = fromDate as RelativeDate;
        break;
      }
      case DateRangeType.RelativeBefore: {
        attrs.window = toDate as RelativeDate;
        break;
      }
      case DateRangeType.Between:
      case DateRangeType.On: {
        if (this.isRelativeDate(fromDate)) {
          attrs.rolling = true;
        }
        attrs.from = fromDate;
        attrs.to = toDate;
        break;
      }
      case DateRangeType.Since: {
        attrs.from = fromDate;
        break;
      }
      default: {
        return null;
      }
    }

    return new DateRange(attrs);
  }

  public type: DateRangeType;
  public from?: BaseDate;
  public to?: BaseDate;
  public window?: RelativeDate;
  public rolling?: boolean;

  constructor(attributes: Partial<DateRange> = {}) {
    attributes = attributes || {};
    this.type = attributes.type || DateRangeType.RelativeAfter;
    switch (this.type) {
      case DateRangeType.RelativeAfter:
      case DateRangeType.RelativeBefore: {
        if (attributes.window) {
          this.window = attributes.window;
        } else {
          this.window = this.getDefaultWindow();
        }
        break;
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        this.from = attributes.from;
        this.to = attributes.to;
        if (isBoolean(attributes.rolling)) {
          this.rolling = attributes.rolling;
        }
        break;
      }
      case DateRangeType.Since: {
        this.from = attributes.from;
        break;
      }
      default: {
        break;
      }
    }
  }

  public getCalendarDates(): CalendarDateRange {
    let fromMoment;
    let toMoment;
    if (this.isRelativeWindow()) {
      fromMoment = moment.utc(DateRange.convertRelativeDateToCalendarDate(this.window));
      toMoment = getCurrentMoment({localized: true}).startOf(`day`);
    } else if (this.rolling) {
      fromMoment = moment.utc(DateRange.convertRelativeDateToCalendarDate(this.from as RelativeDate));
      toMoment = moment.utc(DateRange.convertRelativeDateToCalendarDate(this.to as RelativeDate));
    } else {
      fromMoment = moment.utc(DateRange.convertAbsoluteDateToCalendarDate(this.from as AbsoluteDate));
      toMoment = moment.utc(DateRange.convertAbsoluteDateToCalendarDate(this.to as AbsoluteDate));
    }
    return {
      from: fromMoment.format(CALENDAR_DATE_FORMAT),
      to: toMoment.format(CALENDAR_DATE_FORMAT),
    };
  }

  public getIntervalDays(): number {
    const {to, from} = this.getCalendarDates();
    return moment(to).diff(moment(from), TimeUnit.Day);
  }

  public getBookmarkFormat(): BookmarkDateRange {
    const bookmarkDateRange: Partial<BookmarkDateRange> = {type: this.type};
    switch (this.type) {
      case DateRangeType.RelativeAfter: {
        bookmarkDateRange.from_date = this.window;
        bookmarkDateRange.to_date = NOW;
        break;
      }
      case DateRangeType.RelativeBefore: {
        bookmarkDateRange.to_date = this.window;
        break;
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        bookmarkDateRange.from_date = this.from;
        bookmarkDateRange.to_date = this.to;
        break;
      }
      case DateRangeType.Since: {
        bookmarkDateRange.from_date = this.from;
        bookmarkDateRange.to_date = NOW;
        break;
      }
      default: {
        return null;
      }
    }

    return bookmarkDateRange as BookmarkDateRange;
  }

  public getDefaultWindow(): RelativeDate {
    return {
      unit: TimeUnit.Hour,
      value: 96,
    };
  }

  public getDateRangeTypeOptions(): DateRangeType[] {
    return [DateRangeType.RelativeAfter, DateRangeType.Between, DateRangeType.Since, DateRangeType.On];
  }

  public getTimeUnitOptions(): TimeUnit[] {
    return [TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month];
  }

  public getAttributes(): Partial<DateRange> {
    return pick(this, DATE_RANGE_ATTRIBUTES);
  }

  public mergeAttributes(attributes: Partial<DateRange>): this {
    return new (this.constructor as any)(merge(this.getAttributes(), attributes));
  }

  public equals(otherRange: DateRange): boolean {
    return (
      this.type === otherRange.type &&
      isEqual(this.from, otherRange.from) &&
      isEqual(this.to, otherRange.to) &&
      isEqual(this.window, otherRange.window) &&
      this.rolling === otherRange.rolling
    );
  }

  public isValid(maxHistoryDays?: number): boolean {
    if (maxHistoryDays) {
      return this.getIntervalDays() <= maxHistoryDays;
    }

    return this._isValid();
  }

  public isWithinMaxHistoryLimit(maxHistoryDays: number): boolean {
    if (!this.isValid()) {
      return false;
    }
    if (maxHistoryDays === FEATURE_GATES_UNLIMITED) {
      return true;
    }

    let fromMoment = null;
    if (this.isRelativeWindow()) {
      const {value, unit} = this.window;
      fromMoment = getCurrentMoment({localized: true}).subtract(value, unit);
    } else {
      fromMoment = (SPECIAL_DATE_RESOLVERS[this.from as AbsoluteDate] || (() => moment(this.from as AbsoluteDate)))();
    }
    const minDayMoment = getCurrentMoment({localized: true}).subtract(maxHistoryDays, `days`);
    return fromMoment.isSameOrAfter(minDayMoment);
  }

  public isValidPastRelativeDate(date: RelativeDate): boolean {
    if (!date) {
      return false;
    }
    const {value, unit} = date;
    return this.getTimeUnitOptions().includes(unit) && isNumber(value) && value >= 1;
  }

  public isValidRollingRelativeDate(date: RelativeDate): boolean {
    if (!date) {
      return false;
    }
    const {value, unit} = date;
    return this.getTimeUnitOptions().includes(unit) && unit === TimeUnit.Day && isNumber(value) && value >= 0;
  }

  public isValidAbsoluteDate(date: AbsoluteDate): boolean {
    return date in SPECIAL_DATE_RESOLVERS || !isNil(parseDate(date));
  }

  public getCohortApiFormat(): ApiDateRange {
    // Cohorts has a slightly different format for relative before. this should work for other APIs as well.
    switch (this.type) {
      case DateRangeType.RelativeAfter: {
        return {window: this.window};
      }
      case DateRangeType.RelativeBefore: {
        return {to_date: this.window};
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        return {
          from_date: this.from,
          to_date: this.to,
        };
      }
      case DateRangeType.Since: {
        return {from_date: this.from};
      }
      default: {
        return null;
      }
    }
  }

  public getCalendarMaxDate(): string {
    return null;
  }

  public isRelativeWindow(): boolean {
    return [DateRangeType.RelativeAfter, DateRangeType.RelativeBefore].includes(this.type);
  }

  public getCalendarDate(date: BaseDate): string {
    if (this.isRelativeWindow() || !date) {
      return null;
    }

    if (this.rolling) {
      return DateRange.convertRelativeDateToCalendarDate(date as RelativeDate);
    } else if (date in SPECIAL_DATE_RESOLVERS) {
      const value = [START_OF_YESTERDAY, END_OF_YESTERDAY].includes(date as AbsoluteDate) ? 1 : 0;
      return DateRange.convertRelativeDateToCalendarDate({unit: TimeUnit.Day, value});
    } else {
      return date as string;
    }
  }

  public getCalendarFromDate(): string {
    return this.getCalendarDate(this.from);
  }

  public getCalendarToDate(): string {
    return this.getCalendarDate(this.to);
  }

  public isRolling(): boolean {
    return !!this.rolling || [START_OF_TODAY, START_OF_YESTERDAY].includes(this.from as AbsoluteDate);
  }

  public getLabel(): string {
    let parts = [];
    switch (this.type) {
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        const dates = [this.from, this.to];
        if (this.from === START_OF_TODAY && this.to === END_OF_TODAY) {
          parts = [`today`];
        } else if (this.from === START_OF_YESTERDAY && this.to === END_OF_YESTERDAY) {
          parts = [`yesterday`];
        } else {
          if (this.rolling) {
            const [first, second] = dates.map((date) => {
              return formatDate(parseDate(DateRange.convertRelativeDateToCalendarDate(date as RelativeDate)));
            });
            parts = [this.type, first, `and`, second, `(rolling)`];
          } else {
            const [first, second] = dates.map((date) => formatDate(parseDate(date as AbsoluteDate)));
            parts = [this.type, first, `and`, second];
          }
        }
        break;
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Since:
        if (this.rolling) {
          const date = formatDate(parseDate(DateRange.convertRelativeDateToCalendarDate(this.from as RelativeDate)));
          parts = [this.type, date, `(rolling)`];
        } else {
          parts = [this.type, formatDate(parseDate(this.from as AbsoluteDate))];
        }
        break;
      case DateRangeType.RelativeAfter:
      case DateRangeType.RelativeBefore: {
        const window = this.window;
        parts = [this.type, `${window.value > 1 ? `${window.value} ` : ``}${pluralize(window.unit, window.value)}`];
        break;
      }
      default:
        break;
    }
    return parts.join(` `);
  }

  // get a display name in the same format as the date range presets
  public getDisplayText(): string {
    switch (this.type) {
      case DateRangeType.RelativeAfter: {
        return `Last ${this.window.value} ${pluralize(this.window.unit, this.window.value)}`;
      }
      case DateRangeType.Between:
      case DateRangeType.NotBetween: {
        if (this.from === START_OF_TODAY && this.to === END_OF_TODAY) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Today];
        } else if (this.from === START_OF_YESTERDAY && this.to === END_OF_YESTERDAY) {
          return PRESET_DATE_RANGE_DISPLAY_NAMES[PresetDateRange.Yesterday];
        }
        if (this.rolling) {
          const from = DateRange.convertRelativeDateToCalendarDate(this.from as RelativeDate);
          const to = DateRange.convertRelativeDateToCalendarDate(this.to as RelativeDate);
          return `${formatDate(parseDate(from))} — ${formatDate(parseDate(to))} (rolling)`;
        }
        return `${formatDate(parseDate(this.from as AbsoluteDate))} — ${formatDate(
          parseDate(this.to as AbsoluteDate),
        )}`;
      }
      case DateRangeType.On:
      case DateRangeType.NotOn:
      case DateRangeType.Since: {
        // On or Since
        if (this.rolling) {
          const from = DateRange.convertRelativeDateToCalendarDate(this.from as RelativeDate);
          return `${capitalize(this.type)} ${formatDate(parseDate(from))} (rolling)`;
        } else {
          return `${capitalize(this.type)} ${formatDate(parseDate(this.from as AbsoluteDate))}`;
        }
      }
      default:
        // note: RelativeBefore is not supported
        return this.getLabel();
    }
  }

  private _isValid(): boolean {
    if (!this.getDateRangeTypeOptions().includes(this.type)) {
      return false;
    }

    switch (this.type) {
      case DateRangeType.On:
      case DateRangeType.NotOn:
        if (this.rolling) {
          return this.isValidRollingRelativeDate(this.from as RelativeDate);
        } else {
          return this.isValidAbsoluteDate(this.from as AbsoluteDate);
        }
      case DateRangeType.Since:
        return this.isValidAbsoluteDate(this.from as AbsoluteDate);
      case DateRangeType.Between:
      case DateRangeType.NotBetween:
        if (this.rolling) {
          return (
            this.isValidRollingRelativeDate(this.from as RelativeDate) &&
            this.isValidRollingRelativeDate(this.to as RelativeDate)
          );
        } else {
          return (
            this.isValidAbsoluteDate(this.from as AbsoluteDate) && this.isValidAbsoluteDate(this.to as AbsoluteDate)
          );
        }
      case DateRangeType.RelativeBefore:
      case DateRangeType.RelativeAfter: {
        return this.isValidPastRelativeDate(this.window);
      }
      default:
        return false;
    }
  }
}
